import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy, Download, Trash2, Upload } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Pill } from "../../components/Pill/Pill";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useToast } from "../../components/Toast/toastContext";
import { formatByteCount, formatBytes } from "../../lib/bytes";
import { fileDownloader } from "../../lib/download";
import { ApiError, describeError } from "../../lib/errors";
import { playerCloudSaveContentPath } from "./api";
import { formatDate, formatRelative, formatUTC } from "./formatTime";
import {
  CloudSaveDeleteDialog,
  CloudSaveUploadDialog,
} from "./PlayerCloudSaveDialogs";
import type { Player, PlayerCloudSave } from "./types";
import styles from "./PlayerCloudSaveCard.module.css";

interface Props {
  projectId: string;
  player: Player;
  /** The slot's facts, or undefined while the first read is in flight. */
  data: PlayerCloudSave | undefined;
  loading: boolean;
  failed: boolean;
  /** Whether THIS SERVER has a bucket. Without one the facts still show;
   *  the three actions do not work and say so. */
  storageConfigured: boolean;
  /** The project's ceiling on one save, for the upload dialog. */
  maxBytes: number;
  /**
   * After a download, an upload or a delete: refetch the card and the feed.
   * Resolves once the refetch is through; the dialogs wait for it.
   */
  onChanged: () => void | Promise<void>;
}

/**
 * The player's cloud save: the slot's facts and the three things support
 * does with it — download the file, put another one in its place, delete it.
 *
 * Like the Linked-accounts card, its query is outside the page's render gate,
 * so every state — loading, failed, empty, no bucket — is drawn inside the
 * card body and the editor below stays usable.
 */
export function PlayerCloudSaveCard({
  projectId,
  player,
  data,
  loading,
  failed,
  storageConfigured,
  maxBytes,
  onChanged,
}: Props) {
  const { push } = useToast();
  const [dialog, setDialog] = useState<"upload" | "delete" | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const save = data?.exists ? data : null;

  async function download() {
    setDownloading(true);
    try {
      const name = await fileDownloader.download(
        playerCloudSaveContentPath(projectId, player.id),
        `${player.id}.save`,
      );
      if (!mounted.current) return;
      push({ variant: "success", message: `Downloaded ${name}.` });
      // A download is a journal entry, so the feed has something new.
      void onChanged();
    } catch (err) {
      if (!mounted.current) return;
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : "Couldn't reach Social Hub API.",
      });
    } finally {
      if (mounted.current) setDownloading(false);
    }
  }

  // The «copied» tick's timer, cleared with the card, and a mounted flag
  // for the download: a copy or a download followed by leaving the page
  // would otherwise set state — and raise a toast — on a card that is gone.
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  async function copySha(sha: string) {
    try {
      await navigator.clipboard.writeText(sha);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
      push({ variant: "success", message: "SHA-256 copied to clipboard." });
    } catch {
      push({ variant: "danger", message: "Couldn't copy — clipboard unavailable." });
    }
  }

  const storageAlert = !storageConfigured && (
    <Alert variant="warn" title="Storage is not configured on this server">
      <code>SH_GCS_BUCKET</code> is empty, so the game API answers{" "}
      <code>cloud_save_unavailable</code> and nothing can be downloaded or
      uploaded here. The project&apos;s switch is on — see Config → Cloud
      saves.
    </Alert>
  );

  let body: ReactNode;
  let helper: ReactNode = null;
  if (loading || (!data && !failed)) {
    body = <CloudSaveSkeleton />;
  } else if (!data) {
    // A failed background refetch keeps what is on screen; the note is for
    // when the card has nothing else to show — and then no buttons either,
    // since none of the three has a save to act on.
    body = (
      <div className={styles.note} role="status">
        Could not load the cloud save. Reload the page to try again.
      </div>
    );
  } else if (!save) {
    body = (
      <>
        {storageAlert}
        <div
          className={
            storageConfigured ? styles.note : `${styles.note} ${styles.noteBelowAlert}`
          }
        >
          No cloud save yet. The game has not uploaded one, or it was deleted.
        </div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            icon={<Upload size={13} aria-hidden="true" />}
            disabled={!storageConfigured}
            onClick={() => setDialog("upload")}
          >
            Upload…
          </Button>
        </div>
      </>
    );
    helper = storageConfigured
      ? "Uploading here seeds the slot: the game finds it on its next status check and downloads it."
      : "Metadata is still read from the database; the bytes live in the bucket the server cannot reach.";
  } else {
    const fromAdmin = save.source === "admin";
    body = (
      <>
        {storageAlert}
        <div
          className={
            storageConfigured ? styles.grid : `${styles.grid} ${styles.belowAlert}`
          }
        >
          <div className={styles.kv}>
            <div className={styles.kvKey}>Updated</div>
            <div className={styles.kvVal}>{formatRelative(save.updated_at)}</div>
            <div className={styles.gloss}>{formatUTC(save.updated_at)}</div>
          </div>
          <div className={styles.kv}>
            <div className={styles.kvKey}>Size</div>
            <div className={styles.kvVal}>{formatBytes(save.size_bytes)}</div>
            <div className={styles.gloss}>
              {formatByteCount(save.size_bytes)} · {save.content_encoding}
            </div>
          </div>
          <div className={styles.kv}>
            <div className={styles.kvKey}>Source</div>
            <div className={styles.kvVal}>
              {fromAdmin ? (
                <Pill variant="info" size="sm">
                  Admin upload
                </Pill>
              ) : (
                <Pill variant="neutral" size="sm">
                  Game client
                </Pill>
              )}
            </div>
            <div className={styles.gloss}>
              {fromAdmin
                ? (save.admin_email ?? "an operator")
                : "last write came from the SDK"}
            </div>
          </div>
          <div className={styles.kv}>
            <div className={styles.kvKey}>Created</div>
            <div className={styles.kvVal}>{formatDate(save.created_at)}</div>
            <div className={styles.gloss}>first upload</div>
          </div>
        </div>
        {/* The mockup's «storage not configured» block (2c) goes from the
            grid straight to the actions: the hash is the token of a
            protocol this server cannot take part in. */}
        {storageConfigured && (
          <div className={`${styles.sha} ${styles.kv}`}>
            <div className={styles.kvKey}>SHA-256</div>
            <div className={styles.shaVal}>
              <span className={styles.hash}>{save.sha256}</span>
              <button
                type="button"
                className={styles.copyBtn}
                title="Copy"
                aria-label="Copy SHA-256"
                onClick={() => void copySha(save.sha256)}
              >
                {copied ? (
                  <Check size={13} aria-hidden="true" />
                ) : (
                  <Copy size={13} aria-hidden="true" />
                )}
              </button>
            </div>
            <div className={styles.gloss}>
              Hash of the save as the game sees it — the client&apos;s conflict
              token.
            </div>
          </div>
        )}
        <div className={styles.actions}>
          <Button
            variant="secondary"
            icon={<Download size={13} aria-hidden="true" />}
            disabled={!storageConfigured}
            loading={downloading}
            onClick={() => void download()}
          >
            Download
          </Button>
          <Button
            variant="secondary"
            icon={<Upload size={13} aria-hidden="true" />}
            disabled={!storageConfigured}
            onClick={() => setDialog("upload")}
          >
            Replace…
          </Button>
          <span className={styles.spacer} />
          <Button
            variant="danger-outline"
            icon={<Trash2 size={13} aria-hidden="true" />}
            disabled={!storageConfigured}
            onClick={() => setDialog("delete")}
          >
            Delete
          </Button>
        </div>
      </>
    );
    helper = storageConfigured
      ? "The file is exactly what the game uploaded — the server never opens it. Replacing or deleting it here is seen by the game on its next upload (a conflict, then a re-download) or on its next status check."
      : "Metadata is still read from the database; the bytes live in the bucket the server cannot reach.";
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Cloud save</h3>
        <span className={styles.cardSub}>cloud_saves · one slot per player</span>
      </div>
      <div className={styles.cardBody}>{body}</div>
      {helper && <div className={styles.cardHelper}>{helper}</div>}

      {dialog === "upload" && data && (
        <CloudSaveUploadDialog
          projectId={projectId}
          player={player}
          current={data}
          maxBytes={maxBytes}
          onClose={() => setDialog(null)}
          onChanged={onChanged}
          onDone={(message) => {
            setDialog(null);
            push({ variant: "success", message });
          }}
        />
      )}
      {dialog === "delete" && save && (
        <CloudSaveDeleteDialog
          projectId={projectId}
          player={player}
          save={save}
          onClose={() => setDialog(null)}
          onChanged={onChanged}
          onDone={() => {
            setDialog(null);
            push({ variant: "success", message: "Cloud save deleted." });
          }}
        />
      )}
    </div>
  );
}

/** The same shape as the card it stands in for: four facts and the hash row. */
function CloudSaveSkeleton() {
  const keys = [60, 40, 50, 55];
  const vals = ["80%", "60%", "70%", "65%"];
  return (
    <>
      <div className={styles.grid}>
        {keys.map((w, i) => (
          <div className={styles.kv} key={i}>
            <Skeleton width={w} height={9} />
            <Skeleton width={vals[i]} height={12} style={{ marginTop: 6 }} />
          </div>
        ))}
      </div>
      <div className={styles.sha}>
        <Skeleton width={60} height={9} />
        <Skeleton width="100%" height={12} style={{ marginTop: 6 }} />
      </div>
    </>
  );
}
